/**
 * Typed event emitter for incoming WebSocket frames (T-008).
 *
 * Lives alongside `eventBus.ts` (T-007's auth bus) but is intentionally
 * separate: the auth bus carries imperative side-effects (forced
 * re-auth, validation warnings) while this bus carries server-pushed
 * domain events. Keeping them apart means the WS layer never imports
 * the auth event vocabulary, and the auth refresh orchestrator never
 * accidentally listens for `device_state` frames.
 *
 * Downstream consumers — in particular T-023's TanStack Query
 * invalidator — subscribe via `wsEventBus.on(eventName, listener)` and
 * never touch the raw `MessageEvent`. The shape below mirrors F-300's
 * WS message envelope contract (see F-300 technical spec § "WebSocket
 * protocol").
 *
 * Naming note: this bus uses the **task-spec event names** (`device_state`,
 * `suggestion_new`, `routine_triggered`) rather than F-300's historical
 * `device_state_changed` / `suggestion_created` / `routine_executed`.
 * The naming alignment is part of F-300 ↔ F-310 contract freeze — when
 * the freeze lands the wire names will be the ones here. Until then the
 * raw-frame handler in `websocket.ts` accepts both spellings and
 * normalises them to this surface, so subscribers never see drift.
 *
 * Per F-310 functional spec § 4.2 (Device cards) + § 12 (disconnect UX)
 * and technical spec § 9 (WebSocket strategy).
 */

/**
 * The full event vocabulary the WS layer publishes. Listeners are
 * strongly-typed against the payload via the `WSEvent` helper below.
 *
 * Payloads are intentionally minimal — listeners do **not** receive
 * the full server frame envelope (`{ type, ... }`). They get just the
 * data they need to act, which keeps `wsEventBus` consumers decoupled
 * from on-wire envelope churn.
 */
export interface WSEventMap {
  /** Device state changed on HA → translated → pushed. T-023 invalidates `["devices", device_id]`. */
  device_state: { device_id: string; new_state: Record<string, unknown> };
  /** A new automation suggestion landed. T-023 invalidates `["suggestions"]`. */
  suggestion_new: { suggestion_id: string };
  /** An existing suggestion was edited / accepted / dismissed elsewhere. */
  suggestion_updated: { suggestion_id: string };
  /** A routine fired (manual run or schedule). Surfaces in the routines list. */
  routine_triggered: { routine_id: string };
  /** Server accepted our auth frame — internal signal used by `useWebSocket` to flip to `open`. */
  auth_ok: void;
  /** Server rejected our auth frame — `useWebSocket` closes + reconnects (token may be stale). */
  auth_error: { reason: string };
  /** Server sent a `hello` frame post-connect (session_id, versions). Useful for diagnostics. */
  hello: { session_id: string; ha_version?: string; mirlai_version?: string };
}

/** Convenience: union of all event names. */
export type WSEventName = keyof WSEventMap;

/**
 * Listener signature for a given event. Events with a `void` payload
 * receive no argument (mirrors `addEventListener` ergonomics).
 */
export type WSEventListener<E extends WSEventName> = WSEventMap[E] extends void
  ? () => void
  : (payload: WSEventMap[E]) => void;

/**
 * Untyped storage shape — internal only. The public surface above
 * preserves payload types per event; the internal `Set<Listener>` map
 * has to be erased to a common signature so we can keep one container
 * per event name without 8 separate fields.
 */
type AnyListener = (payload?: unknown) => void;

class WSEventBus {
  private listeners = new Map<WSEventName, Set<AnyListener>>();

  /**
   * Subscribe to `event`. Returns the unsubscribe function so React
   * effects can return it directly from their cleanup body.
   *
   * Idempotent on re-subscribe of the same function reference: the
   * underlying `Set` dedupes, so double-`on()` calls don't double-fire.
   */
  on<E extends WSEventName>(event: E, listener: WSEventListener<E>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as AnyListener);
    return () => {
      set?.delete(listener as AnyListener);
    };
  }

  /**
   * Explicit unsubscribe — useful for non-React consumers that capture
   * the listener reference outside the closure returned by `on()`.
   */
  off<E extends WSEventName>(event: E, listener: WSEventListener<E>): void {
    this.listeners.get(event)?.delete(listener as AnyListener);
  }

  /**
   * Fire all listeners for `event`. Throws from a single listener are
   * swallowed (and logged) so one bad subscriber can't sink siblings.
   *
   * Snapshots the listener set before iterating so a listener can
   * unsubscribe itself mid-emit without skipping later listeners.
   *
   * The overload preserves payload typing: events with a `void` payload
   * are called as `emit("auth_ok")`, others as `emit("device_state", {...})`.
   */
  emit<E extends WSEventName>(
    event: WSEventMap[E] extends void ? E : never,
  ): void;
  emit<E extends WSEventName>(
    event: WSEventMap[E] extends void ? never : E,
    payload: WSEventMap[E],
  ): void;
  emit<E extends WSEventName>(event: E, payload?: WSEventMap[E]): void {
    const set = this.listeners.get(event);
    if (!set || set.size === 0) return;
    for (const listener of Array.from(set)) {
      try {
        listener(payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[wsEventBus] listener for "${event}" threw`, err);
      }
    }
  }

  /** Drop every subscriber. Test-only helper — production never calls. */
  clearAll(): void {
    this.listeners.clear();
  }

  /** Count subscribers for an event. Test-only helper. */
  count(event: WSEventName): number {
    return this.listeners.get(event)?.size ?? 0;
  }
}

/**
 * Singleton bus. Lives on the module so every importer sees the same
 * instance; tests can reset via `wsEventBus.clearAll()` in `beforeEach`.
 */
export const wsEventBus = new WSEventBus();
